import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box} from '@mui/material';

import {WorkforceProfileWizardStepContent} from '../WorkforceProfileWizard/steps/WorkforceProfileWizardStepContent';
import type {WorkforceProfileCandidateField} from '../WorkforceProfileWizard/steps/workforceProfileWizardStepTypes';
import {WorkforceProfileCreatePathChoice} from '../WorkforceProfileWizard/WorkforceProfileCreatePathChoice';
import {WorkforceProfileWizardStepper} from '../WorkforceProfileWizard/WorkforceProfileWizardStepper';

import {CreateWorkforceProfileModalActions} from './CreateWorkforceProfileModalActions';
import {WorkforceProfileDocumentUploadBranch} from './WorkforceProfileDocumentUploadBranch';
import EmpionDialog from '../../../../../components/EmpionDialog/EmpionDialog';
import {CompanyContext} from '../../../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiRead} from '../../../../../hooks/useApiRead';
import {useApiWrite} from '../../../../../hooks/useApiWrite';
import {GetRoutes} from '../../../../../lib/api/GET/GetRoutes';
import {Post} from '../../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../../lib/api/POST/PostRoutes';
import {
  ResearchWorkforceProfileDraftResponse,
  WorkforceProfileRequestBodies,
} from '../../../../../lib/api/RequestBodies/features/company/workforce/WorkforceProfileRequestBodies';
import {fileToBase64} from '../../../../../utils/fileToBase64';
import {Department} from '../../../profile/types';
import {mapDepartmentTargetProfilesResponse} from '../../serializers/DepartmentTargetProfileSerializers';
import {mapResearchWorkforceProfileDraftResponse} from '../../serializers/WorkforceProfiles/mapResearchWorkforceProfileDraftResponse';
import {DepartmentTargetProfileAssignment} from '../../types/DepartmentTargetProfileTypes';
import type {WorkforceProfileAnalysisDataInput} from '../../types/workforceProfileDetailsTypes';
import {
  GUIDED_WIZARD_STEP_ORDER,
  type WorkforceProfileGuidedStepId,
  type WorkforceProfileInputMode,
  type WorkforceProfileWizardState,
} from '../../types/workforceProfileWizardTypes';
import {mergeResearchDraftResponse} from '../../utils/WorkforceProfileWizard/mergeResearchDraftResponse';
import {
  clearWizardDraft,
  createEmptyProfileAnalysisDataInput,
  createInitialWizardState,
  persistWizardDraft,
} from '../../utils/WorkforceProfileWizard/workforceProfileWizardStateUtils';
import {
  validateGuidedReview,
  validateGuidedSetup,
  validateGuidedStep,
} from '../../utils/WorkforceProfileWizard/workforceProfileWizardValidation';

export interface Props {
  open: boolean;
  companyId: string;
  departments: Department[];
  onClose: () => void;
  onCreated: () => void;
}

export const CreateWorkforceProfileModal = ({open, companyId, departments, onClose, onCreated}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const {company} = useContext(CompanyContext);
  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.createModal'});
  const {t: tWizard} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.wizard'});
  const {t: tShared} = useTranslation();

  const defaultEmployerName = company.data?.name ?? '';

  const [wizardState, setWizardState] = useState<WorkforceProfileWizardState>(() =>
    createInitialWizardState(defaultEmployerName)
  );
  const [analysisInput, setAnalysisInput] = useState<WorkforceProfileAnalysisDataInput>(
    createEmptyProfileAnalysisDataInput
  );
  const [guidedStepId, setGuidedStepId] = useState<WorkforceProfileGuidedStepId>('setup');
  const [profileFile, setProfileFile] = useState<File | null>(null);

  const {setup, lookup, inputMode} = wizardState;
  const departmentId = setup.departmentId;

  const {
    data: departmentTargetProfiles = [],
    isLoading: isTargetProfilesLoading,
    isError: isTargetProfilesError,
  } = useApiRead<DepartmentTargetProfileAssignment[]>({
    path: GetRoutes.companyDepartmentTargetProfiles(companyId, departmentId),
    camelCaseOverride: mapDepartmentTargetProfilesResponse,
    options: {
      queryKey: ['companyDepartmentTargetProfiles', companyId, departmentId, 'create-modal'],
      enabled: open && !!companyId && !!departmentId,
    },
  });

  const {
    makeRequest: createProfile,
    isLoading: isCreating,
    error: createError,
  } = useApiWrite<
    Post.CreateCompanyWorkforceProfile,
    WorkforceProfileRequestBodies[Post.CreateCompanyWorkforceProfile]
  >({
    method: 'post',
    path: PostRoutes[Post.CreateCompanyWorkforceProfile](companyId),
    onSuccess: () => {
      clearWizardDraft(companyId);
      onCreated();
      handleClose();
      dispatchMessage(tShared('shared.formSubmit.success'), 'success');
    },
  });

  const applyResearchDraftResponse = useCallback(
    (response: ResearchWorkforceProfileDraftResponse) => {
      const merged = mergeResearchDraftResponse(response);
      setAnalysisInput(merged.analysisInput);
      setWizardState((previous) => ({
        ...previous,
        suggestedFieldKeys: merged.suggestedFieldKeys,
        researchCompleted: true,
        lookup: {
          ...previous.lookup,
          personName: response.workforceProfileResearch.personName ?? previous.lookup.personName,
          profileUrl: response.workforceProfileResearch.profileUrl ?? previous.lookup.profileUrl,
          currentEmployerName:
            response.workforceProfileResearch.currentEmployerName ?? previous.lookup.currentEmployerName,
          currentJobTitle: response.workforceProfileResearch.currentJobTitle ?? previous.lookup.currentJobTitle,
          location: response.workforceProfileResearch.location ?? previous.lookup.location,
        },
      }));
      dispatchMessage(tWizard('research.success'), 'success');
    },
    [dispatchMessage, tWizard]
  );

  const {
    makeRequest: researchDraft,
    isLoading: isResearchLoading,
    error: researchError,
  } = useApiWrite<Post.ResearchWorkforceProfileDraft, ResearchWorkforceProfileDraftResponse>({
    method: 'post',
    path: PostRoutes[Post.ResearchWorkforceProfileDraft](companyId),
    camelCaseOverride: mapResearchWorkforceProfileDraftResponse,
    onSuccess: applyResearchDraftResponse,
  });

  const resetForm = useCallback(() => {
    setWizardState(createInitialWizardState(defaultEmployerName));
    setAnalysisInput(createEmptyProfileAnalysisDataInput());
    setGuidedStepId('setup');
    setProfileFile(null);
  }, [defaultEmployerName]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  useEffect(() => {
    if (createError) {
      dispatchMessage(tShared('shared.formSubmit.creationError'), 'error');
    }
  }, [createError, dispatchMessage, tShared]);

  useEffect(() => {
    if (researchError) {
      dispatchMessage(tWizard('research.error'), 'error');
    }
  }, [dispatchMessage, researchError, tWizard]);

  useEffect(() => {
    if (open && companyId && wizardState.inputMode === 'guided') {
      persistWizardDraft(companyId, wizardState, analysisInput);
    }
  }, [analysisInput, companyId, open, wizardState]);

  const handleSelectInputMode = (mode: WorkforceProfileInputMode) => {
    setWizardState((previous) => ({...previous, inputMode: mode}));
    if (mode === 'guided') {
      setGuidedStepId('setup');
    }
  };

  const handleBack = () => {
    if (inputMode === 'document') {
      setWizardState((previous) => ({...previous, inputMode: null}));
      return;
    }

    if (guidedStepId === 'setup') {
      setWizardState((previous) => ({...previous, inputMode: null}));
      return;
    }

    const index = GUIDED_WIZARD_STEP_ORDER.indexOf(guidedStepId);
    setGuidedStepId(GUIDED_WIZARD_STEP_ORDER[Math.max(0, index - 1)]);
  };

  const handleNext = () => {
    const index = GUIDED_WIZARD_STEP_ORDER.indexOf(guidedStepId);
    setGuidedStepId(GUIDED_WIZARD_STEP_ORDER[Math.min(GUIDED_WIZARD_STEP_ORDER.length - 1, index + 1)]);
  };

  const updateCandidateField = useCallback((key: WorkforceProfileCandidateField, value: string) => {
    setAnalysisInput((previous) => ({
      ...previous,
      candidateProfile: {
        ...previous.candidateProfile,
        [key]: value,
      },
    }));
  }, []);

  const handleGenerateSuggestions = async () => {
    try {
      await researchDraft({
        workforceProfileResearch: {
          personName: lookup.personName.trim(),
          profileUrl: lookup.profileUrl.trim() || undefined,
          currentEmployerName: lookup.currentEmployerName.trim() || undefined,
          currentJobTitle: lookup.currentJobTitle.trim() || undefined,
          location: lookup.location.trim() || undefined,
          seniority: setup.seniority || undefined,
          companyDepartmentId: setup.departmentId || undefined,
          targetProfileId: setup.targetProfileId || undefined,
        },
      });
    } catch (error) {
      console.error('Error generating workforce profile research draft:', error);
    }
  };

  const isUploadFormValid = !!setup.departmentId && !!setup.seniority && !!setup.targetProfileId && !!profileFile;

  const canProceedGuided = useMemo(() => {
    if (guidedStepId === 'setup') {
      return validateGuidedSetup(setup, lookup);
    }
    return validateGuidedStep(guidedStepId, analysisInput);
  }, [analysisInput, guidedStepId, lookup, setup]);

  const reviewIssues = useMemo(() => validateGuidedReview(analysisInput), [analysisInput]);

  const handleSubmitUpload = async () => {
    if (!isUploadFormValid || !profileFile) {
      return;
    }

    try {
      const profileDocument = await fileToBase64(profileFile);
      await createProfile({
        companyWorkforceProfile: {
          companyDepartmentId: setup.departmentId,
          companyDepartmentTargetProfileId: setup.targetProfileId,
          seniority: setup.seniority,
          inputSource: 'document',
          profileDocument,
        },
      });
    } catch (error) {
      console.error('Error creating workforce profile:', error);
    }
  };

  const handleSubmitGuided = async () => {
    if (reviewIssues.length) {
      dispatchMessage(tWizard('review.blocked'), 'error');
      return;
    }

    try {
      await createProfile({
        companyWorkforceProfile: {
          companyDepartmentId: setup.departmentId,
          companyDepartmentTargetProfileId: setup.targetProfileId,
          seniority: setup.seniority,
          inputSource: 'structured',
          profileAnalysisDataInput: analysisInput,
        },
      });
    } catch (error) {
      console.error('Error creating structured workforce profile:', error);
    }
  };

  const metadataProps = {
    departments,
    currentLanguage,
    setup,
    lookup,
    departmentTargetProfiles,
    isTargetProfilesLoading,
    isTargetProfilesError,
    onSetupChange: (nextSetup: typeof setup) => setWizardState((previous) => ({...previous, setup: nextSetup})),
    onLookupChange: (nextLookup: typeof lookup) => setWizardState((previous) => ({...previous, lookup: nextLookup})),
  };

  const showPathChoice = !inputMode;
  const isGuided = inputMode === 'guided';
  const isDocument = inputMode === 'document';
  const isReviewStep = isGuided && guidedStepId === 'review';

  return (
    <EmpionDialog open={open} onClose={handleClose} showCloseButton maxWidth='lg' title={t('title')}>
      {showPathChoice && <WorkforceProfileCreatePathChoice onSelect={handleSelectInputMode} />}
      {isDocument && (
        <WorkforceProfileDocumentUploadBranch
          {...metadataProps}
          profileFile={profileFile}
          onProfileFileChange={setProfileFile}
        />
      )}
      {isGuided && (
        <Box sx={{mt: 1}}>
          <WorkforceProfileWizardStepper activeStepId={guidedStepId} />
          <WorkforceProfileWizardStepContent
            stepId={guidedStepId}
            wizardState={wizardState}
            analysisInput={analysisInput}
            isResearchLoading={isResearchLoading}
            reviewIssues={reviewIssues}
            onSetupChange={metadataProps.onSetupChange}
            onLookupChange={metadataProps.onLookupChange}
            onAnalysisInputChange={setAnalysisInput}
            onCandidateFieldChange={updateCandidateField}
            onGenerateSuggestions={handleGenerateSuggestions}
            onEditStep={setGuidedStepId}
            departments={departments}
            currentLanguage={currentLanguage}
            departmentTargetProfiles={departmentTargetProfiles}
            isTargetProfilesLoading={isTargetProfilesLoading}
            isTargetProfilesError={isTargetProfilesError}
          />
        </Box>
      )}

      {!showPathChoice && (
        <CreateWorkforceProfileModalActions
          isDocument={isDocument}
          isGuided={isGuided}
          isReviewStep={isReviewStep}
          isUploadFormValid={isUploadFormValid}
          canProceedGuided={canProceedGuided}
          isCreating={isCreating}
          hasReviewIssues={!!reviewIssues.length}
          onBack={handleBack}
          onNext={handleNext}
          onSubmitUpload={handleSubmitUpload}
          onSubmitGuided={handleSubmitGuided}
        />
      )}
    </EmpionDialog>
  );
};
